import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AnalysisService } from 'src/app/services/analysis.service';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { LoadingService } from 'src/app/services/loading.service';
import { DateSelectionComponent } from '../../date-selection/date-selection.component';

import { NgIf } from '@angular/common';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {GridComponent, TooltipComponent, LegendComponent, TitleComponent} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
    selector: 'app-pareto',
    templateUrl: './pareto.component.html',
    styleUrls: ['./pareto.component.scss'],
    imports: [DateSelectionComponent, NgIf, NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })]
})
export class ParetoComponent implements OnInit, OnDestroy {
  
 @Input() DatePickerIndex:number = 0;
  
  chartOption: any;
  formattedData: any[] = [];
  filteredData: any[] = [];
  filterData: any[] = [];
  filter = new FormControl('');
  paertoSub$: Subscription;
  paretoData: any = {};

  plant:string;
  currentShift: string = 'True';
  line: string = 'ALL';
  severity: string = 'ALL';
  public fromDateString: string;
  public toDateString: string;

  fg_hdr: FormGroup = new FormGroup('');
  manualInput = false;
  labelSeperator:string = '->';
  showDetail = false;
  showByDuration = false;
  showChart = false;

  constructor(
    private route: ActivatedRoute,
    private svcAnalysis: AnalysisService,
    private zone: NgZone,
    private loadingService: LoadingService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.plant = this.route.snapshot.paramMap.get('plant');
    this.route.queryParams.subscribe(params => this.severity = params['Severity']);
    this.route.queryParams.subscribe(params => this.line = params['Line']);

    setTimeout(() => {
      if (this.fromDateString && this.toDateString) {
        this.getParetoData();
      }
    });
  }
 
  applyFilter() {
    const hiddenLabels = this.filter.value || [];
    this.filteredData = this.formattedData.filter(rec => !hiddenLabels.includes(rec.label));
    this.buildChartOption();
  }

  clearFilter(level:number = 1) {
    this.getParetoData();
  }

  onDateChange(e: any) {
  this.fromDateString = e.fromDate;
  this.toDateString = e.toDate;
  this.currentShift = e.currentShift;
  this.getParetoData();
}

  getParetoData() {
    this.loadingService.show() 
 
    if (this.paertoSub$) {
      this.paertoSub$.unsubscribe();
    }

    this.paertoSub$ = this.svcAnalysis
      .pareto(
        this.plant,
        this.currentShift,
        this.fromDateString + ' 00:00:00',
        this.toDateString + ' 23:59:59.999',
        this.line,
        this.severity
      )
      .subscribe({
        next: (data) => {
          this.paretoData = data.Body;
          const myFormattedData: any[] = [];

          this.formattedData = [];

          this.paretoData.forEach(element => {
            const myLabel = element.ComponentDescription + this.labelSeperator + element.DefectDescription;
            const myValue = element.TotalIssues;

            myFormattedData.push({
              label: myLabel,
              value: myValue
            });
          });

          this.formattedData = myFormattedData;
          this.applyFilter();
          this.showChart = this.filteredData.length > 0;
          this.loadingService.hide();
        },
        error: (err) => {
          console.log(err);
          this.showChart = false;
          this.loadingService.hide();
        }
      });
  }

  setGrouping(grouping: string) {
    this.showByDuration = grouping === 'duration';
    this.getParetoData();
  }

  buildChartOption() {
    const labels = this.filteredData.map(x => x.label);
    const defectValues = this.filteredData.map(x => Number(x.value) || 0);

    const total = defectValues.reduce((sum, val) => sum + val, 0);
    let running = 0;

    const cumulativePercent = defectValues.map(val => {
      running += val;
      return total > 0 ? Number(((running / total) * 100).toFixed(2)) : 0;
    });

    const shouldRotate = labels.length >= 12;

    this.chartOption = {
      title: {
        text: '',
        left: 'center',
        top: 10
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#444',
          fontSize: 11,
        },
        formatter: (params: any[]) => {
          if (!params?.length) return '';

          const name = params[0].axisValueLabel || params[0].axisValue;
          const rows = params.map((p: any) => {
            const marker = p.seriesType === 'line'
              ? `<span style="display:inline-block;width:10px;height:10px;margin-right:6px;border-radius:50%;background:${p.color};"></span>`
              : `<span style="display:inline-block;width:10px;height:10px;margin-right:6px;background:${p.color};"></span>`;

            const value = p.seriesType === 'line'
              ? `${Number(p.value).toFixed(2)}%`
              : Number(p.value).toLocaleString();

            return `
              <div style="display:flex;justify-content:space-between;gap:16px;">
                <span>${marker}${p.seriesName}</span>
                <span><b>${value}</b></span>
              </div>
            `;
          }).join('');

          return `
            <div style="min-width:220px;">
              <div style="margin-bottom:6px;">${name}</div>
              ${rows}
            </div>
          `;
        }
      },
      grid: {left:60, right: 80, top: 30, bottom: shouldRotate ? 170 : 70, containLabel: false},
      xAxis: {
        type: 'category',
        name: 'Component + Defect',
        nameLocation: 'middle',
        nameGap: shouldRotate ? 140 : 60,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#ccc'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          interval: 0,
          rotate: shouldRotate ? 90 : 0,
          hideOverlap: true,
          color: '#666',
          fontSize: 11,
          formatter: (value: string) => {
            const max = 40;
            return value && value.length > max
              ? value.substring(0, max) + '…'
              : value;
          }
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 14
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Defects',
          nameLocation: 'middle',
          nameRotate: 90,
          nameGap: 30,
          min: 0,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666',
            fontSize: 12
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#e9edf5',
              type: 'solid'
            }
          },
          nameTextStyle: {
            color: '#666',
            fontSize: 14
          }
        },
        {
          type: 'value',
          name: 'Cumulative Percentage',
          nameLocation: 'middle',
          nameRotate: 270,
          nameGap: 55,
          min: 0,
          max: 100,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666',
            fontSize: 12,
            formatter: '{value}%'
          },
          splitLine: {
            show: false
          },
          nameTextStyle: {
            color: '#666',
            fontSize: 14
          }
        }
      ],
      series: [
        {
          name: 'Defects',
          type: 'bar',
          yAxisIndex: 0,
          barMaxWidth: 16,
          itemStyle: {
          borderRadius: [8, 8, 0, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
            { offset: 0, color: '#c7cdfa' },
            { offset: 0.35, color: '#8f98db' },
            { offset: 0.7, color: '#6670b8' },
            { offset: 1, color: '#4a538f' }
          ])
        },
          data: defectValues
        },
        {
          name: 'Cumulative %',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 1,
            color: '#666'
          },
          itemStyle: {
            color: '#666'
          },
          data: cumulativePercent
        }
      ]
    };
  }

  drilldown(e: any) {
    console.debug('Drilldown!!!', e);
    this.zone.run(() => {
      this.getParetoData();
    });
  }

  toggleDetail() {
    this.showDetail = !this.showDetail;
    this.getParetoData();
  }
  
  ngOnDestroy(): void {
    if (this.paertoSub$) {
      this.paertoSub$.unsubscribe();
    }
  }
}